import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { joinAnswerText } from './join-answer-text'
import type { ResolvedPart } from './use-interleaved-parts'

// Rough rendered width of one line of chat text, and how long each line keeps
// a folded answer on screen at least. A one-liner still gets a floor so it
// reads as a pause.
const CHARS_PER_LINE = 70
const HOLD_MS_PER_LINE = 450
const MIN_HOLD_MS = 1600

/** The least time a live answer that a tool call just folded stays where it was, scaled by its line count. */
export function computeAnswerHoldMs(text: string): number {
  const lines = text
    .split('\n')
    .filter((line) => line.trim())
    .reduce((total, line) => total + Math.ceil(line.length / CHARS_PER_LINE), 0)
  return Math.max(MIN_HOLD_MS, lines * HOLD_MS_PER_LINE)
}

export interface HeldAnswer {
  /** The text when it folded; the parts' current text wins while they're still in the message. */
  text: string
  revealKey: string
  /** The folded text parts, kept out of the fold until the hold ends. */
  partKeys: ReadonlySet<string>
}

/**
 * Held answers first wait out their minimum hold, then wait for an answer to
 * replace them. The answer streaming when the hold ended (or null) started
 * during it, so it doesn't count as a replacement.
 */
type HoldPhase = { kind: 'holding' } | { kind: 'awaiting-replacement'; liveKeyAtHoldEnd: string | null }

interface HeldAnswers {
  answers: Array<HeldAnswer>
  /** When every held answer has had its minimum hold. */
  holdUntil: number
  phase: HoldPhase
}

const NO_HELD_ANSWERS: Array<HeldAnswer> = []

interface UseHeldAnswersOptions {
  enabled: boolean
  isStreaming: boolean
  interleavedParts: Array<ResolvedPart>
  trailingTextParts: Array<ResolvedPart & { type: 'text' }>
  finalText: string
  revealKey: string
}

/**
 * Keeps live answers in place after tool calls move them into the fold. Each
 * gets a minimum hold; an answer that starts streaming during it renders below
 * and joins the held ones when it folds too. They all go into the fold
 * together once the hold is over and the next answer starts streaming, or the
 * turn ends.
 */
export function useHeldAnswers({
  enabled,
  isStreaming,
  interleavedParts,
  trailingTextParts,
  finalText,
  revealKey,
}: UseHeldAnswersOptions): Array<HeldAnswer> {
  const [held, setHeld] = useState<HeldAnswers | null>(null)
  const previousRef = useRef({ trailingTextParts, finalText, revealKey })
  const liveAnswerRef = useRef<string | null>(null)
  liveAnswerRef.current = finalText ? revealKey : null

  // Layout effect so the folded text never paints in the fold before the hold
  // takes it back out.
  useLayoutEffect(() => {
    const previous = previousRef.current
    previousRef.current = { trailingTextParts, finalText, revealKey }
    if (!enabled || !previous.finalText) return

    const foldedKeys = new Set(interleavedParts.map((part) => part.stableKey))
    const moved = previous.trailingTextParts.filter((part) => foldedKeys.has(part.stableKey))
    if (moved.length === 0) return

    const answer: HeldAnswer = {
      text: previous.finalText,
      revealKey: previous.revealKey,
      partKeys: new Set(moved.map((part) => part.stableKey)),
    }
    const holdUntil = Date.now() + computeAnswerHoldMs(answer.text)
    setHeld((current) => {
      if (current?.answers.some((existing) => existing.revealKey === answer.revealKey)) return current
      return {
        answers: [...(current?.answers ?? []), answer],
        holdUntil: Math.max(current?.holdUntil ?? 0, holdUntil),
        phase: { kind: 'holding' },
      }
    })
  }, [enabled, interleavedParts, trailingTextParts, finalText, revealKey])

  useEffect(() => {
    if (held?.phase.kind !== 'holding') return
    const timeoutId = window.setTimeout(
      () =>
        setHeld(
          (current) =>
            current && {
              ...current,
              phase: { kind: 'awaiting-replacement', liveKeyAtHoldEnd: liveAnswerRef.current },
            },
        ),
      Math.max(0, held.holdUntil - Date.now()),
    )
    return () => window.clearTimeout(timeoutId)
  }, [held])

  const phase = held?.phase
  const isReplaced =
    phase?.kind === 'awaiting-replacement' &&
    !!finalText &&
    revealKey !== phase.liveKeyAtHoldEnd &&
    !held?.answers.some((answer) => answer.revealKey === revealKey)
  const isReleased = !held || (phase?.kind === 'awaiting-replacement' && (isReplaced || !isStreaming))
  useEffect(() => {
    if (held && isReleased) setHeld(null)
  }, [held, isReleased])

  const answers = isReleased || !held ? NO_HELD_ANSWERS : held.answers
  // A sentence can keep streaming into a folded part (see `foldTextParts`),
  // so show each held answer's parts as they are now.
  return useMemo(
    () =>
      answers.flatMap((answer) => {
        const text = joinAnswerText(
          interleavedParts.filter(
            (part): part is ResolvedPart & { type: 'text' } =>
              part.type === 'text' && answer.partKeys.has(part.stableKey),
          ),
        )
        // Its parts were joined into a later step's text that carries the
        // sentence on, and that text shows it now.
        if (!text) return []
        return [text !== answer.text ? { ...answer, text } : answer]
      }),
    [answers, interleavedParts],
  )
}
